import { useState } from 'react';
import { useProjectContext } from '../contexts/ProjectContext';
import { useCreateProject, useUpdateProject, useDeleteProject } from '../hooks/useProjects';
import { ProjectCard } from '../components/Projects/ProjectCard';
import { ProjectFormModal } from '../components/Projects/ProjectFormModal';
import type { Project, ProjectSummary } from '../types';

export function Projects() {
  const { projects, currentProject, setCurrentProjectId, isLoading, error } = useProjectContext();
  const createProjectMutation = useCreateProject();
  const updateProjectMutation = useUpdateProject();
  const deleteProjectMutation = useDeleteProject();

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<ProjectSummary | null>(null);

  const isSaving = createProjectMutation.isPending || updateProjectMutation.isPending || deleteProjectMutation.isPending;

  // Create new project
  const handleCreate = async (data: { name: string; description?: string }) => {
    try {
      const project = await createProjectMutation.mutateAsync({ name: data.name, description: data.description });
      setCurrentProjectId(project.id);
      setShowForm(false);
    } catch (err) {
      console.error('Create project error:', err);
    }
  };

  // Edit project
  const handleEdit = (project: ProjectSummary) => {
    setEditingProject(project as unknown as Project);
    setShowForm(true);
  };

  // Update project
  const handleUpdate = async (data: { name: string; description?: string }) => {
    if (!editingProject) return;

    try {
      await updateProjectMutation.mutateAsync({ id: editingProject.id, ...data });
      setShowForm(false);
      setEditingProject(null);
    } catch (err) {
      console.error('Update project error:', err);
    }
  };

  // Delete project
  const handleDelete = async () => {
    if (!showDeleteConfirm) return;
    try {
      await deleteProjectMutation.mutateAsync(showDeleteConfirm.id);
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Delete project error:', err);
    }
  };

  // Select project
  const handleSelect = (project: ProjectSummary) => {
    setCurrentProjectId(project.id);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Progetti</h2>
          <p className="text-gray-600 mt-1">
            Gestisci i tuoi progetti di acquisto casa
          </p>
        </div>
        <button
          onClick={() => {
            setEditingProject(null);
            setShowForm(true);
          }}
          className="btn-primary"
        >
          + Nuovo Progetto
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error instanceof Error ? error.message : 'Errore nel caricamento'}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && projects.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : projects.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-6xl mb-4">🏠</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Nessun progetto
          </h3>
          <p className="text-gray-600 mb-6">
            Crea il tuo primo progetto per iniziare a gestire le spese della casa.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary"
          >
            Crea il primo progetto
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              isSelected={currentProject?.id === project.id}
              onSelect={() => handleSelect(project)}
              onEdit={() => handleEdit(project)}
              onDelete={() => setShowDeleteConfirm(project)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <ProjectFormModal
          project={editingProject}
          onSubmit={editingProject ? handleUpdate : handleCreate}
          onClose={() => {
            setShowForm(false);
            setEditingProject(null);
          }}
          isLoading={isSaving}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Elimina progetto
            </h3>
            <p className="text-gray-600 mb-4">
              Sei sicuro di voler eliminare <strong>{showDeleteConfirm.name}</strong>?
              <br />
              <span className="text-red-600 text-sm">
                Questa azione non può essere annullata.
              </span>
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="btn-secondary"
                disabled={isSaving}
              >
                Annulla
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isSaving}
              >
                {isSaving ? 'Eliminazione...' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
